import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { KeyRound, Bed, Star, Coffee, Sparkles, ChevronRight, LogIn, QrCode, Utensils, PhoneCall, Send } from "lucide-react";

export default function GuestApp() {
    const [step, setStep] = useState("login"); // login | dashboard | service | feedback
    const [resCode, setResCode] = useState("");
    const [phone, setPhone] = useState("");
    const [booking, setBooking] = useState(null);
    const [service, setService] = useState({ request_type: "housekeeping", description: "" });
    const [fb, setFb] = useState({ rating: 5, comment: "" });
    const [digitalKey, setDigitalKey] = useState(null);
    const [installPrompt, setInstallPrompt] = useState(null);

    // Register PWA service worker and capture install prompt
    useEffect(() => {
        // Inject manifest link (guest scope only)
        let link = document.querySelector('link[rel="manifest"]');
        const prevHref = link?.getAttribute("href");
        if (!link) {
            link = document.createElement("link");
            link.rel = "manifest";
            document.head.appendChild(link);
        }
        link.href = "/manifest-guest.json";
        let themeMeta = document.querySelector('meta[name="theme-color"]');
        const prevTheme = themeMeta?.getAttribute("content");
        if (themeMeta) themeMeta.setAttribute("content", "#0A1C3E");

        // Register service worker
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.register("/sw-guest.js", { scope: "/guest" }).catch(() => null);
        }

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            if (prevHref) link.href = prevHref;
            if (themeMeta && prevTheme) themeMeta.setAttribute("content", prevTheme);
        };
    }, []);

    const installApp = async () => {
        if (!installPrompt) return;
        await installPrompt.prompt();
        setInstallPrompt(null);
    };

    const doLookup = async () => {
        try {
            const { data } = await api.post("/guest-portal/lookup", { reservation_code: resCode, phone });
            setBooking(data);
            setStep("dashboard");
            toast.success(`Welcome ${data.reservation.guest_name}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const requestService = async () => {
        try {
            await api.post("/guest-portal/service-request", { ...service, reservation_id: booking.reservation.id });
            toast.success("Request sent to hotel staff");
            setService({ request_type: "housekeeping", description: "" });
            setStep("dashboard");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitFeedback = async () => {
        try {
            await api.post("/guest-portal/feedback", { ...fb, reservation_id: booking.reservation.id, rating: Number(fb.rating) });
            toast.success("Thank you for your feedback!");
            setFb({ rating: 5, comment: "" });
            setStep("dashboard");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const getDigitalKey = async () => {
        try {
            const { data } = await api.get(`/guest-portal/digital-key/${booking.reservation.id}`);
            setDigitalKey(data);
            toast.success("Digital key issued");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (step === "login") {
        return (
            <div className="min-h-screen bg-gradient-to-b from-[#0A1C3E] to-[#132D5C] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-sm p-6 shadow-2xl border-none">
                    <div className="text-center mb-6">
                        <div className="w-14 h-14 bg-[#0A1C3E] rounded-md mx-auto flex items-center justify-center">
                            <span className="text-[#C5B08A] font-display font-bold text-2xl">M</span>
                        </div>
                        <div className="mt-3 font-display font-bold text-slate-900">MERIDIAN</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Guest Experience</div>
                    </div>
                    <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Booking ID</Label>
                                <Input value={resCode} onChange={e => setResCode(e.target.value)} placeholder="Received via email/SMS" data-testid="guest-res-code" /></div>
                            <div><Label className="text-xs uppercase tracking-wider">Phone <span className="text-red-500">*</span></Label>
                                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="guest-phone" /></div>
                            <div className="text-xs text-slate-500">Phone must match the booking for verification.</div>
                            <Button onClick={doLookup} disabled={!resCode || !phone} className="w-full bg-[#0A1C3E] rounded-sm" data-testid="guest-lookup">
                                <LogIn className="w-4 h-4 mr-1" /> Access My Stay
                            </Button>
                        </div>
                </Card>
            </div>
        );
    }

    const res = booking.reservation;
    const hotel = booking.hotel;

    return (
        <div className="min-h-screen bg-slate-50">
            <Toaster position="top-right" richColors />
            {/* Header */}
            <div className="bg-[#0A1C3E] text-white p-4 sticky top-0 z-10">
                <div className="max-w-md mx-auto flex justify-between items-center">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5B08A]">{hotel?.name}</div>
                        <div className="font-display font-semibold">Welcome, {res.guest_name}</div>
                    </div>
                    <button onClick={() => { setBooking(null); setStep("login"); }} className="text-xs text-white/60">Sign out</button>
                </div>
            </div>
            {installPrompt && (
                <div className="max-w-md mx-auto p-3">
                    <div className="p-3 bg-[#C5B08A] text-[#0A1C3E] rounded-md flex justify-between items-center">
                        <div className="text-xs">
                            <div className="font-bold">Install Meridian</div>
                            <div className="opacity-80">Add to home screen for instant access</div>
                        </div>
                        <button onClick={installApp} className="text-xs font-bold bg-[#0A1C3E] text-[#C5B08A] px-3 py-1.5 rounded" data-testid="pwa-install">Install</button>
                    </div>
                </div>
            )}

            <div className="max-w-md mx-auto p-4 space-y-4">
                {step === "dashboard" && (
                    <>
                        {/* Room info card */}
                        <Card className="p-5 border border-slate-200 rounded-md shadow-none bg-white">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Your Room</div>
                                    <div className="text-3xl font-display font-bold text-slate-900 mt-1">Room</div>
                                    <div className="text-xs text-slate-500 mt-1">{res.check_in_date?.slice(0, 10)} → {res.check_out_date?.slice(0, 10)}</div>
                                </div>
                                <span className={`status-badge ${res.status === "checked_in" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-indigo-100 text-indigo-800 border-indigo-200"}`}>
                                    {res.status?.replace(/_/g, " ")}
                                </span>
                            </div>
                            {res.status === "checked_in" && (
                                digitalKey ? (
                                    <div className="mt-4 p-3 bg-[#0A1C3E] rounded text-center text-white">
                                        <QrCode className="w-8 h-8 mx-auto text-[#C5B08A]" />
                                        <div className="font-mono font-bold text-lg mt-2">{digitalKey.key_id}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-white/60 mt-1">Digital Key · Room {digitalKey.room_number}</div>
                                    </div>
                                ) : (
                                    <Button onClick={getDigitalKey} className="w-full mt-4 bg-[#C5B08A] text-[#0A1C3E] hover:bg-[#D4C39F] rounded-sm" data-testid="get-digital-key">
                                        <KeyRound className="w-4 h-4 mr-1" /> Get Digital Key
                                    </Button>
                                )
                            )}
                        </Card>

                        {/* Quick actions */}
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { icon: Sparkles, label: "Housekeeping", type: "housekeeping" },
                                { icon: Utensils, label: "Room Service", type: "room_service" },
                                { icon: Coffee, label: "Laundry", type: "laundry" },
                                { icon: PhoneCall, label: "Concierge", type: "concierge" },
                            ].map(a => (
                                <button key={a.type} onClick={() => { setService({ request_type: a.type, description: "" }); setStep("service"); }}
                                    className="p-4 bg-white border border-slate-200 rounded-md hover:border-[#0A1C3E] transition-colors text-left"
                                    data-testid={`quick-${a.type}`}>
                                    <a.icon className="w-5 h-5 text-[#C5B08A]" />
                                    <div className="mt-2 text-sm font-semibold text-slate-900">{a.label}</div>
                                </button>
                            ))}
                        </div>

                        <button onClick={() => setStep("feedback")} className="w-full p-4 bg-white border border-slate-200 rounded-md hover:border-[#0A1C3E] flex items-center justify-between" data-testid="rate-stay">
                            <div className="flex items-center gap-3">
                                <Star className="w-5 h-5 text-[#C5B08A]" />
                                <div>
                                    <div className="text-sm font-semibold">Rate Your Stay</div>
                                    <div className="text-xs text-slate-500">Share your experience</div>
                                </div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                        </button>

                        {/* Folio summary */}
                        <Card className="p-4 border border-slate-200 rounded-md shadow-none">
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Folio</div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-slate-600">Total</span><span className="font-mono font-semibold">₹{Number(res.total || 0).toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-slate-600">Paid</span><span className="font-mono text-emerald-700">₹{Number(res.paid || 0).toLocaleString()}</span></div>
                                <div className="flex justify-between pt-2 border-t"><span className="text-slate-600 font-semibold">Balance</span><span className={`font-mono font-bold ${res.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>₹{Number(res.balance || 0).toLocaleString()}</span></div>
                            </div>
                        </Card>
                    </>
                )}

                {step === "service" && (
                    <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Service Request</div>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Type</Label>
                                <Select value={service.request_type} onValueChange={v => setService({ ...service, request_type: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{["housekeeping", "room_service", "laundry", "wake_up", "spa", "concierge"].map(x => <SelectItem key={x} value={x}>{x.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Details</Label>
                                <Textarea rows={4} value={service.description} onChange={e => setService({ ...service, description: e.target.value })} placeholder="Tell us what you need..." data-testid="service-desc" /></div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setStep("dashboard")} className="flex-1">Cancel</Button>
                                <Button onClick={requestService} disabled={!service.description} className="flex-1 bg-[#0A1C3E]" data-testid="service-submit">Send</Button>
                            </div>
                        </div>
                    </Card>
                )}

                {step === "feedback" && (
                    <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Rate Your Stay</div>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Rating</Label>
                                <div className="flex gap-1 mt-2">
                                    {[1, 2, 3, 4, 5].map(r => (
                                        <button key={r} onClick={() => setFb({ ...fb, rating: r })} data-testid={`rate-${r}`}>
                                            <Star className={`w-8 h-8 ${r <= fb.rating ? "fill-[#C5B08A] text-[#C5B08A]" : "text-slate-300"}`} />
                                        </button>
                                    ))}
                                </div></div>
                            <div><Label className="text-xs uppercase tracking-wider">Comment</Label>
                                <Textarea rows={4} value={fb.comment} onChange={e => setFb({ ...fb, comment: e.target.value })} placeholder="Tell us about your experience..." data-testid="fb-comment" /></div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setStep("dashboard")} className="flex-1">Cancel</Button>
                                <Button onClick={submitFeedback} className="flex-1 bg-[#0A1C3E]" data-testid="fb-submit">Submit</Button>
                            </div>
                        </div>
                    </Card>
                )}
            </div>
        </div>
    );
}
